/**
 * Searchable grid of a customer's prior mockups (customerDesignMockups).
 * Selecting an entry reuses the stored URL on the current line.
 */

import React, { useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { FaTimes, FaSearch } from 'react-icons/fa';
import { LINE_TYPES } from '../firebase/schema';
import {
  fetchCustomerDesignLibrary,
  filterCustomerDesignLibrary,
  getDesignLibrarySubtitle,
  mapDbTypeToLineType
} from '../utils/customerDesignLibrary';

const TYPE_TABS = [
  { key: 'all', label: 'All' },
  { key: LINE_TYPES.SCREEN_PRINT, label: 'Screen Print' },
  { key: LINE_TYPES.EMBROIDERY, label: 'Embroidery' },
  { key: LINE_TYPES.DTF, label: 'Direct To Film' },
  { key: LINE_TYPES.DTG, label: 'Direct To Garment' }
];

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2100;
`;

const Panel = styled.div`
  background: var(--al-bg);
  width: min(1100px, 94vw);
  height: min(780px, 90vh);
  border: 1px solid rgba(255, 255, 255, 0.15);
  display: flex;
  flex-direction: column;
  font-family: 'Montserrat', sans-serif;
  color: white;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 18px;
  z-index: 2;
  padding: 4px;

  &:hover {
    color: var(--al-primary);
  }
`;

const Header = styled.div`
  padding: 16px 44px 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const Toolbar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  background: ${props => (props.$active ? 'rgba(var(--al-primary-rgb), 0.18)' : 'transparent')};
  border: 1px solid ${props => (props.$active ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.2)')};
  color: ${props => (props.$active ? 'var(--al-primary)' : 'rgba(255, 255, 255, 0.75)')};
  padding: 6px 10px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const SearchWrap = styled.label`
  flex: 1;
  min-width: 180px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 6px 10px;
  color: rgba(255, 255, 255, 0.5);

  input {
    flex: 1;
    background: transparent;
    border: none;
    color: var(--al-primary);
    font-family: inherit;
    font-size: 0.85rem;
    font-weight: 600;
    outline: none;
    text-transform: uppercase;
  }
`;

const Grid = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
  align-content: start;
`;

const Card = styled.button`
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 8px;
  cursor: pointer;
  text-align: left;
  color: inherit;
  font-family: inherit;

  &:hover {
    border-color: var(--al-primary);
  }
`;

const Thumb = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const CardTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardMeta = styled.div`
  margin-top: 2px;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.5);
  text-transform: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Empty = styled.p`
  grid-column: 1 / -1;
  margin: 24px 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 0.85rem;
`;

const ConfirmOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  padding: 20px;
`;

const ConfirmBox = styled.div`
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 20px;
  max-width: 360px;
  width: 100%;
  text-align: center;
`;

const ConfirmText = styled.p`
  margin: 0 0 16px;
  font-size: 0.85rem;
  line-height: 1.4;
  text-transform: none;
  color: rgba(255, 255, 255, 0.9);
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
`;

const ConfirmButton = styled.button`
  background: ${props => (props.$primary ? 'var(--al-primary)' : '#333')};
  color: ${props => (props.$primary ? '#111' : 'white')};
  border: none;
  padding: 8px 14px;
  cursor: pointer;
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
`;

function entryType(entry) {
  return mapDbTypeToLineType(entry?.lineSnapshot?.type);
}

function CustomerMockupsModal({ isOpen, onClose, customerId, onSelect, hasExistingMockup = false }) {
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [pendingEntry, setPendingEntry] = useState(null);

  useEffect(() => {
    if (!isOpen || !customerId) {
      setLibrary([]);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    fetchCustomerDesignLibrary(customerId, { forceRefresh: true })
      .then((entries) => {
        if (!cancelled) setLibrary(entries.filter((entry) => entry.mockupUrl));
      })
      .catch((error) => {
        console.error('Error loading customer mockups:', error);
        if (!cancelled) setLibrary([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId]);

  useEffect(() => {
    if (!isOpen) {
      setPendingEntry(null);
      setSearch('');
      setTypeFilter('all');
    }
  }, [isOpen]);

  const counts = useMemo(() => {
    const next = { all: library.length };
    for (const tab of TYPE_TABS) {
      if (tab.key === 'all') continue;
      next[tab.key] = library.filter((entry) => entryType(entry) === tab.key).length;
    }
    return next;
  }, [library]);

  const visible = useMemo(() => {
    const byType = typeFilter === 'all'
      ? library
      : library.filter((entry) => entryType(entry) === typeFilter);

    const trimmed = search.trim();
    if (!trimmed) return byType;
    if (trimmed.length < 2) {
      const q = trimmed.toLowerCase();
      return byType.filter((entry) => (entry.displayName || '').toLowerCase().includes(q));
    }
    const matched = new Set(filterCustomerDesignLibrary(byType, trimmed, 80).map((entry) => entry.id));
    return byType.filter((entry) => matched.has(entry.id));
  }, [library, typeFilter, search]);

  const applyEntry = (entry) => {
    if (!entry) return;
    onSelect(entry);
    setPendingEntry(null);
    onClose();
  };

  const handleCardClick = (entry) => {
    if (hasExistingMockup) {
      setPendingEntry(entry);
      return;
    }
    applyEntry(entry);
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <CloseButton type="button" onClick={onClose} aria-label="Close">
          <FaTimes />
        </CloseButton>
        <Header>
          <Title>Customer mockups</Title>
          <Toolbar>
            <Tabs>
              {TYPE_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  type="button"
                  $active={typeFilter === tab.key}
                  onClick={() => setTypeFilter(tab.key)}
                >
                  {tab.label} ({counts[tab.key] || 0})
                </Tab>
              ))}
            </Tabs>
            <SearchWrap>
              <FaSearch />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search designs..."
              />
            </SearchWrap>
          </Toolbar>
        </Header>
        <Grid>
          {loading ? (
            <Empty>Loading mockups…</Empty>
          ) : !customerId ? (
            <Empty>Select a customer to browse their mockups.</Empty>
          ) : visible.length === 0 ? (
            <Empty>No mockups found for this customer.</Empty>
          ) : (
            visible.map((entry) => (
              <Card
                key={entry.id}
                type="button"
                onClick={() => handleCardClick(entry)}
              >
                <Thumb>
                  <img src={entry.mockupUrl} alt={entry.displayName || 'Mockup'} />
                </Thumb>
                <CardTitle title={entry.displayName}>{entry.displayName || 'Untitled'}</CardTitle>
                <CardMeta>
                  {getDesignLibrarySubtitle(entry) || (entry.sourceOrderNumber ? `PO ${entry.sourceOrderNumber}` : 'Prior job')}
                </CardMeta>
              </Card>
            ))
          )}
        </Grid>
        {pendingEntry && (
          <ConfirmOverlay onClick={() => setPendingEntry(null)}>
            <ConfirmBox onClick={(e) => e.stopPropagation()}>
              <ConfirmText>
                This line already has a mockup. Replace it with
                {' '}
                <strong>{pendingEntry.displayName || 'this design'}</strong>?
              </ConfirmText>
              <ConfirmActions>
                <ConfirmButton type="button" onClick={() => setPendingEntry(null)}>
                  Cancel
                </ConfirmButton>
                <ConfirmButton type="button" $primary onClick={() => applyEntry(pendingEntry)}>
                  Replace mockup
                </ConfirmButton>
              </ConfirmActions>
            </ConfirmBox>
          </ConfirmOverlay>
        )}
      </Panel>
    </Overlay>
  );
}

export default CustomerMockupsModal;
